import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './UserMenu.css';

interface UserMenuProps {
  onPageChange: (page: string) => void;
}

const UserMenu: React.FC<UserMenuProps> = ({ onPageChange }) => {
  const { user, isAuthenticated, isAdmin, login, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  if (!isAuthenticated) {
    return (
      <button onClick={login} className="login-btn">
        <span className="login-icon">👤</span>
        Sign In
      </button>
    );
  }

  // Get user initials for avatar
  const getUserInitials = (name: string): string => {
    const parts = name.split('@')[0].split('.');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const initials = user?.userDetails ? getUserInitials(user.userDetails) : '??';
  const displayName = user?.userDetails?.split('@')[0] || 'User';

  return (
    <div className="user-menu" ref={menuRef}>
      <button 
        className="user-menu-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="User menu"
        aria-expanded={isOpen}
      >
        <span className="user-avatar" title={user?.userDetails}>
          {initials}
        </span>
        <span className="user-name">{displayName}</span>
        <span className={`chevron ${isOpen ? 'open' : ''}`}>▼</span>
      </button>

      {isOpen && (
        <div className="user-menu-dropdown">
          <div className="user-info">
            <div className="user-avatar-large">{initials}</div>
            <p className="user-email">{user?.userDetails}</p>
            <p className="user-provider">
              via {user?.identityProvider || 'Microsoft'}
            </p>
          </div>
          
          <hr className="menu-divider" />
          
          {isAdmin && (
            <>
              <div className="menu-actions">
                <button 
                  onClick={(e) => {
                    e.preventDefault();
                    console.log('UserMenu: Create Blog clicked');
                    onPageChange('create-blog');
                    setIsOpen(false);
                  }} 
                  className="menu-item"
                >
                  <span className="menu-icon">✏️</span>
                  Create Blog Post
                </button>
                <button 
                  onClick={(e) => {
                    e.preventDefault();
                    console.log('UserMenu: Manage Posts clicked');
                    onPageChange('manage-posts');
                    setIsOpen(false);
                  }} 
                  className="menu-item"
                >
                  <span className="menu-icon">📝</span>
                  Manage Posts
                </button>
              </div>
              
              <hr className="menu-divider" />
            </>
          )}
          
          <button onClick={logout} className="logout-btn">
            <span className="logout-icon">🚪</span>
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
